// Main vuex store - imports submodules

import { format as urlFormat } from 'url'
import Vue from 'vue'
import Vuex from 'vuex'

import api from '@/api'
import sqs from './modules/sqs'

import { AuthSPInfoApi } from '@/api/auth'

Vue.use(Vuex)

const debug = process.env.NODE_ENV !== 'production'

// initial state
const state = {
  // client-side cache of previously requested data in JSON format, by API URL
  _apiCache: {},
  authSPInfo: {}
}

// getters
const getters = {
  authSPInfo: state => state.authSPInfo,

  isLoggedIn: state => state.authSPInfo.user_id !== undefined,
  isSPAdmin: state => state.authSPInfo.is_admin === true,
  authorizedDBNs: state => state.authSPInfo.authorized_dbns || [],

  /**
   * Helper method to check if metadata in given namespace exists
   *
   * Usage: getters.isMetadataLoaded(getterName)
   *
   * @param {string} getterName - namespaced getter to retrieve existing metadata object
   * @returns {boolean} - True if given metadata exists, otherwise false
   */
  isMetadataLoaded: (state, getters) => (getterName) => {
    const metadata = getters[getterName]
    return (metadata != null) && metadata.length > 0
  },
  /**
   * Helper method to get data from client-side cache for given URL
   *
   * Notes:
   *  - Does not make new API request (may be undefined)
   *  - Does not cache, and any other getters using this won't have watching for updates of data
   *
   * Usage: getters.apiDataForUrl(url)
   *
   * @param {string} url - of previous API request
   * @returns {Object} - JSON data for previous API request if it exists, otherwise undefined
   */
  apiCacheDataForUrl: (state) => (url) => {
    return (url == null) ? undefined : state._apiCache[urlFormat(url)]
  }
}

// actions
const actions = {
  /**
   * Helper method to get data from a TSV/CSV file, with client-side caching
   *
   * @param {string} url - location to get data from
   */
  async getDsvData (context, url) {
    const formattedUrl = urlFormat(url)
    let data = context.getters.apiCacheDataForUrl(formattedUrl)
    if (data === undefined) {
      data = await api.getDsvData(formattedUrl)
      context.commit('setApiCacheDataForUrl', {url: formattedUrl, data: data})
    }
    return data
  },
  /**
   * Helper method to get data from an api abstraction, with client-side caching
   *
   * @param {Object} dataApi - api abstraction object
   */
  async getApiData (context, dataApi) {
    const formattedUrl = urlFormat(dataApi.url)
    let data = context.getters.apiCacheDataForUrl(formattedUrl)
    if (data === undefined) {
      data = await dataApi.getData()
      context.commit('setApiCacheDataForUrl', {url: formattedUrl, data: data})
    }
    return data
  },
  /**
   * Helper method to get Student Profile Authorization info from an api abstraction, with client-side caching
   */
  async getAuthSPInfo (context) {
    const dataApi = new AuthSPInfoApi()
    const data = await context.dispatch('getApiData', dataApi)
    // If not logged in yet, 401 will return no data, so don't save it
    if (data !== undefined) {
      context.commit('setAuthSPInfo', data)
    }
  }
}

// mutations
const mutations = {
  setApiCacheDataForUrl (state, {url, data}) {
    state._apiCache[urlFormat(url)] = data
  },
  setAuthSPInfo (state, data) {
    state.authSPInfo = data
  }
}

export default new Vuex.Store({
  state,
  getters,
  actions,
  mutations,
  modules: {
    sqs
  },
  strict: debug
})



// WEBPACK FOOTER //
// ./src/store/index.js